import React, { useState, useEffect, useMemo } from 'react';
import { parseISO, isValid } from 'date-fns';
import { 
  Plus, 
  Filter, 
  Search, 
  Edit, 
  Trash2, 
  Calendar, 
  DollarSign, 
  ArrowUpRight, 
  ArrowDownRight, 
  CheckSquare, 
  Landmark, 
  X,
  RefreshCw,
  FileText,
  Upload,
  Download,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Tag
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from "../components/ui/Button";
import Select from "../components/ui/Select";
import Input from "../components/ui/Input";
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import { downloadCsv, objectsToCsv } from '../utils/csvUtils';
import { Expense } from '../types';
import ExpenseModal from '../components/modals/ExpenseModal';
import ExpenseCsvModal from '../components/modals/ExpenseCsvModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { supabase } from '../lib/supabase';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Expenses: React.FC = () => {
  const { 
    expenses, 
    suppliers, 
    fetchExpenses, 
    addExpense, 
    updateExpense, 
    deleteExpense,
    expenseCategories,
    fetchExpenseCategories
  } = useAppStore();
  
  const { userRole } = useAuth();
  const { toast, ToastComponent } = useToast();
  
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters - Changed default to current month
  const [filters, setFilters] = useState({
    categoryId: 'all',
    paymentMethod: 'all',
    isRecurring: 'all',
    dateRange: 'month', // Changed from 'all' to 'month'
    startDate: '',
    endDate: '',
  });

  // Get current month start and end dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Asegurar que las fechas se manejen correctamente
    startOfMonth.setHours(0, 0, 0, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    
    return {
      start: startOfMonth.toISOString().split('T')[0],
      end: endOfMonth.toISOString().split('T')[0]
    };
  };

  // Initialize filters with current month
  useEffect(() => {
    const { start, end } = getCurrentMonthDates();
    setFilters((prev: typeof filters) => ({
      ...prev,
      startDate: start,
      endDate: end
    }));
  }, []);
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await fetchExpenses();
        await fetchExpenseCategories();
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [fetchExpenses, fetchExpenseCategories]);

  // Keyboard shortcut for adding new expense
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Check if the pressed key is "+" and no input/textarea is focused
      if (event.key === '+' && !['INPUT', 'TEXTAREA'].includes((event.target as HTMLElement)?.tagName)) {
        event.preventDefault();
        handleAddExpense();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);
  
  // Apply filters
  const filteredExpenses = useMemo(() => {
    // Debug: Log current filters and date range
    return expenses.filter(expense => {
      // Validate expense date first
      const expenseDate = parseISO(expense.date);
      if (!isValid(expenseDate)) {
        console.warn('Invalid expense date:', expense.date, 'for expense:', expense.id);
        return false; // Exclude expenses with invalid dates
      }
      
      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesDescription = expense.description.toLowerCase().includes(query);
        
        // Get category name
        let categoryName = '';
        const category = expenseCategories.find(c => c.id === expense.category_id);
        categoryName = category?.name.toLowerCase() || '';
        
        if (!matchesDescription && !categoryName.includes(query)) {
          return false;
        }
      }
      
      // Filter by category
      if (filters.categoryId !== 'all' && expense.category_id !== filters.categoryId) {
        return false;
      }
      
      // Filter by payment method
      if (filters.paymentMethod !== 'all' && expense.payment_method !== filters.paymentMethod) {
        return false;
      }
      
      // Filter by recurring status
      if (filters.isRecurring !== 'all') {
        const isRecurring = filters.isRecurring === 'yes';
        if (expense.is_recurring !== isRecurring) {
          return false;
        }
      }
      
      // Filter by date range
      if (filters.dateRange !== 'all') {
        
        if (filters.dateRange === 'custom') {
          // Custom date range
          if (filters.startDate && filters.endDate) {
            const startDate = parseISO(filters.startDate);
            const endDate = parseISO(filters.endDate);
            
            // Validate filter dates
            if (!isValid(startDate) || !isValid(endDate)) {
              console.warn('Invalid filter dates:', filters.startDate, filters.endDate);
              return true; // Don't filter if filter dates are invalid
            }
            
            endDate.setHours(23, 59, 59); // Include the entire end day
            
            if (expenseDate < startDate || expenseDate > endDate) {
              return false;
            }
          }
        } else {
          // Predefined date ranges
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (filters.dateRange === 'today') {
            const expenseDateOnly = new Date(expenseDate.getTime());
            expenseDateOnly.setHours(0, 0, 0, 0);
            if (expenseDateOnly.getTime() !== today.getTime()) {
              return false;
            }
          } else if (filters.dateRange === 'week') {
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            if (expenseDate < weekAgo) {
              return false;
            }
          } else if (filters.dateRange === 'month') {
            // Usar las fechas de los filtros en lugar de calcular desde today
            const startOfMonth = parseISO(filters.startDate);
            const endOfMonth = parseISO(filters.endDate);
            
            // Validate filter dates
            if (!isValid(startOfMonth) || !isValid(endOfMonth)) {
              console.warn('Invalid month filter dates:', filters.startDate, filters.endDate);
              return true; // Don't filter if filter dates are invalid
            }
            
            // Asegurar que las fechas se comparen correctamente
            startOfMonth.setHours(0, 0, 0, 0);
            endOfMonth.setHours(23, 59, 59, 999);
            
            // Comparar solo las fechas (sin tiempo)
            const expenseDateOnly = new Date(expenseDate.getTime());
            expenseDateOnly.setHours(0, 0, 0, 0);
            
            if (expenseDateOnly < startOfMonth || expenseDateOnly > endOfMonth) {
              return false;
            }
          } else if (filters.dateRange === 'previousMonth') {
            // Calcular fechas del mes anterior
            const now = new Date();
            const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const endOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            
            // Asegurar que las fechas se comparen correctamente
            startOfPreviousMonth.setHours(0, 0, 0, 0);
            endOfPreviousMonth.setHours(23, 59, 59, 999);
            
            // Comparar solo las fechas (sin tiempo)
            const expenseDateOnly = new Date(expenseDate.getTime());
            expenseDateOnly.setHours(0, 0, 0, 0);
            
            if (expenseDateOnly < startOfPreviousMonth || expenseDateOnly > endOfPreviousMonth) {
              return false;
            }
          } else if (filters.dateRange === 'year') {
            const yearAgo = new Date(today);
            yearAgo.setFullYear(today.getFullYear() - 1);
            if (expenseDate < yearAgo) {
              return false;
            }
          }
        }
      }
      
      return true;
    });
  }, [expenses, searchQuery, filters, expenseCategories]);
  
  // Sort expenses by date (most recent first)
// Ordenar del más nuevo al más viejo
const sortedExpenses = useMemo(() => {
  return [...filteredExpenses].sort((a, b) => {
    /* 1️⃣  primero comparamos la fecha (yyyy-mm-dd)  */
    const dateA = parseISO(a.date);
    const dateB = parseISO(b.date);
    
    // Handle invalid dates by putting them at the end
    if (!isValid(dateA) && !isValid(dateB)) return 0;
    if (!isValid(dateA)) return 1;
    if (!isValid(dateB)) return -1;
    
    const diffDate = dateB.getTime() - dateA.getTime();

    /* 2️⃣  si es el mismo día, usamos created_at para que
            el último cargado quede arriba            */
    if (diffDate === 0 && a.created_at && b.created_at) {
      const createdA = parseISO(a.created_at);
      const createdB = parseISO(b.created_at);
      
      if (isValid(createdA) && isValid(createdB)) {
        return createdB.getTime() - createdA.getTime();
      }
    }

    return diffDate;
  });
}, [filteredExpenses]);

  
  // Calculate total amount
  const totalAmount = useMemo(() => {
    return sortedExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  }, [sortedExpenses]);
  
  // Get category name by ID
  const getCategoryName = (categoryId: string): string => {
    const category = expenseCategories.find((c: any) => c.id === categoryId);
    return category?.name || 'Categoría desconocida';
  };
  
  // Get supplier name by ID
  const getSupplierName = (supplierId?: string): string => {
    if (!supplierId) return 'Sin proveedor';
    const supplier = suppliers.find((s: any) => s.id === supplierId);
    return supplier?.name || 'Proveedor desconocido';
  };
  
  // Get payment method display name
  const getPaymentMethodName = (method: string): string => {
    switch (method) {
      case 'cash': return 'Efectivo';
      case 'transfer': return 'Transferencia';
      case 'card': return 'Tarjeta';
      case 'check': return 'Cheque';
      case 'mercadopago': return 'MercadoPago';
      default: return method;
    }
  };
  
  // Get recurrence display name
  const getRecurrenceName = (recurrence?: string): string => {
    if (!recurrence) return 'No';
    
    switch (recurrence) {
      case 'daily': return 'Diaria';
      case 'weekly': return 'Semanal';
      case 'monthly': return 'Mensual';
      case 'quarterly': return 'Trimestral';
      case 'yearly': return 'Anual';
      default: return recurrence;
    }
  };
  
  // Handle adding a new expense
  const handleAddExpense = () => {
    setSelectedExpense(null);
    setIsExpenseModalOpen(true);
  };
  
  // Handle editing an expense
  const handleEditExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsExpenseModalOpen(true);
  };
  
  // Handle deleting an expense
  const handleDeleteExpense = (id: string) => {
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };
  
  // Confirm expense deletion
  const confirmDeleteExpense = async () => {
    if (!itemToDelete) return;
    
    try {
      await deleteExpense(itemToDelete);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };
  
  // Handle saving an expense
  const handleSaveExpense = async (expense: Omit<Expense, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      // Calcular IVA si corresponde
      const computedVatAmount = expense.vat && expense.vat > 0 ? Number((expense.amount * (expense.vat / 100)).toFixed(2)) : 0;
      const payload = {
        ...expense,
        vat_amount: computedVatAmount
      } as Omit<Expense, 'id' | 'created_at' | 'updated_at'>;
      
      if (selectedExpense) {
        await updateExpense(selectedExpense.id, payload);
      } else {
        await addExpense(payload);
        // Show success toast for new expense
        toast.success('Gasto creado exitosamente', 'El gasto ha sido agregado al sistema');
      }
      setIsExpenseModalOpen(false);
    } catch (error) {
      console.error('Error saving expense:', error);
      throw error;
    }
  };
  
  // Calculate metrics for the selected period
  const metrics = useMemo(() => {
    const totalAmount = filteredExpenses.reduce((sum: number, expense: any) => sum + expense.amount, 0);
    const totalVat = filteredExpenses.reduce((sum: number, expense: any) => sum + (expense.vat_amount || 0), 0);
    const totalWithoutVat = totalAmount - totalVat;
    
    // Calculate previous period for comparison
    const getPreviousPeriodExpenses = () => {
      const now = new Date();
      let startDate: Date;
      let endDate: Date;
      
      if (filters.dateRange === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else if (filters.dateRange === 'previousMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      } else if (filters.dateRange === 'week') {
        endDate = new Date(now);
        endDate.setDate(now.getDate() - 7);
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 7);
      } else if (filters.dateRange === 'year') {
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31);
      } else {
        return 0;
      }
      
      return expenses.filter((expense: any) => {
        const expenseDate = parseISO(expense.date);
        if (!isValid(expenseDate)) return false;
        return expenseDate >= startDate && expenseDate <= endDate;
      }).reduce((sum: number, expense: any) => sum + expense.amount, 0);
    };
    
    const previousPeriodAmount = getPreviousPeriodExpenses();
    const changeAmount = totalAmount - previousPeriodAmount;
    const changePercentage = previousPeriodAmount > 0 ? (changeAmount / previousPeriodAmount) * 100 : 0;
    
    // Get top categories
    const categoryTotals: Record<string, number> = {};
    filteredExpenses.forEach((expense: any) => {
      const categoryName = getCategoryName(expense.category_id);
      categoryTotals[categoryName] = (categoryTotals[categoryName] || 0) + expense.amount;
    });
    
    const topCategories = Object.entries(categoryTotals)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([name, amount]) => ({ name, amount }));
    
    return {
      totalAmount,
      totalVat,
      totalWithoutVat,
      changeAmount,
      changePercentage,
      topCategories,
      count: filteredExpenses.length
    };
  }, [filteredExpenses, filters.dateRange, expenses]);

  // Prepare chart data for expenses by category
  const categoryChartData = useMemo(() => {
    const categoryTotals: Record<string, number> = {};
    
    // Calculate totals by category
    sortedExpenses.forEach(expense => {
      const categoryName = getCategoryName(expense.category_id);
      if (!categoryTotals[categoryName]) {
        categoryTotals[categoryName] = 0;
      }
      categoryTotals[categoryName] += expense.amount;
    });
    
    // Prepare chart data
    const labels = Object.keys(categoryTotals);
    const data = Object.values(categoryTotals);
    
    // Define colors
    const backgroundColors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', 
      '#FF9F40', '#8AC926', '#F94144', '#43AA8B', '#277DA1'
    ];
    
    return {
      labels,
      datasets: [
        {
          label: 'Gastos por Categoría',
          data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          borderColor: backgroundColors.slice(0, labels.length).map(color => color),
          borderWidth: 1,
        },
      ],
    };
  }, [sortedExpenses, expenseCategories]);

  // Prepare chart data for expenses by supplier
  const supplierChartData = useMemo(() => {
    const supplierTotals: Record<string, number> = {};
    
    // Calculate totals by supplier
    sortedExpenses.forEach(expense => {
      const supplierName = getSupplierName(expense.supplier_id);
      if (!supplierTotals[supplierName]) {
        supplierTotals[supplierName] = 0;
      }
      supplierTotals[supplierName] += expense.amount;
    });
    
    // Prepare chart data
    const labels = Object.keys(supplierTotals);
    const data = Object.values(supplierTotals);
    
    // Define colors
    const backgroundColors = [
      '#4BC0C0', '#FF6384', '#FFCE56', '#36A2EB', '#9966FF', 
      '#FF9F40', '#8AC926', '#F94144', '#43AA8B', '#277DA1'
    ];
    
    return {
      labels,
      datasets: [
        {
          label: 'Gastos por Proveedor',
          data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          borderColor: backgroundColors.slice(0, labels.length).map(color => color),
          borderWidth: 1,
        },
      ],
    };
  }, [sortedExpenses, suppliers]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          boxWidth: 12,
          padding: 15
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = formatCurrency(context.raw);
            const percentage = Math.round(context.raw * 100 / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) + '%';
            return `${label}: ${value} (${percentage})`;
          }
        }
      }
    }
  };
  
  // Export expenses to CSV
  const exportExpensesToCsv = () => {
    // Use filteredExpenses instead of sortedExpenses to respect all filters
    if (filteredExpenses.length === 0) {
      toast.warning('No hay gastos para exportar', 'No hay gastos en el período seleccionado');
      return;
    }

    // Prepare data for export with proper headers and data cleaning (without Proveedor column)
    const exportData = filteredExpenses.map(expense => ({
      Fecha: formatArgentinaDate(expense.date),
      Categoría: getCategoryName(expense.category_id) || 'Sin categoría',
      Descripción: expense.description || 'Sin descripción',
      Monto: expense.amount || 0,
      IVA: expense.vat > 0 ? `${expense.vat}%` : 'No gravado',
      'Monto IVA': expense.vat_amount || 0,
      'Método de Pago': getPaymentMethodName(expense.payment_method) || 'No especificado',
      Recurrente: expense.is_recurring ? (getRecurrenceName(expense.recurrence) || 'Sí') : 'No',
      'Fecha Creación': expense.created_at ? formatArgentinaDate(expense.created_at) : 'N/A',
      'Fecha Actualización': expense.updated_at ? formatArgentinaDate(expense.updated_at) : 'N/A'
    }));

    // Define headers explicitly to ensure proper order (without Proveedor column)
    const headers = [
      'Fecha',
      'Categoría', 
      'Descripción',
      'Monto',
      'IVA',
      'Monto IVA',
      'Método de Pago',
      'Recurrente',
      'Fecha Creación',
      'Fecha Actualización'
    ];

    // Generate filename with current date and period
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    let periodStr = 'todos';
    
    if (filters.dateRange === 'month') {
      periodStr = 'mes-actual';
    } else if (filters.dateRange === 'previousMonth') {
      periodStr = 'mes-anterior';
    } else if (filters.dateRange === 'week') {
      periodStr = 'ultima-semana';
    } else if (filters.dateRange === 'year') {
      periodStr = 'ultimo-ano';
    } else if (filters.dateRange === 'custom') {
      periodStr = 'personalizado';
    }
    
    const filename = `gastos-${periodStr}-${dateStr}.csv`;
    
    // Convert objects to CSV string first, then download
    const csvString = objectsToCsv(exportData, headers);
    
    try {
      downloadCsv(csvString, filename);
      
      // Show success toast
      toast.success('Exportación exitosa', `Se exportaron ${exportData.length} gastos a ${filename}`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Error en la exportación', 'Hubo un problema al exportar el archivo CSV');
    }
  };

  // Reset all filters
  const resetFilters = () => {
    const { start, end } = getCurrentMonthDates();
    setFilters({
      categoryId: 'all',
      paymentMethod: 'all',
      isRecurring: 'all',
      dateRange: 'month',
      startDate: start,
      endDate: end,
    });
    setSearchQuery('');
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <DollarSign className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Gastos
        </h2>
        <div className="flex gap-2 flex-wrap">
          {userRole !== 'Producción' && (
            <Button
              variant="outline"
              icon={<Filter className="h-5 w-5" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtros
            </Button>
          )}
          <Button
            variant="outline"
            icon={<FileText className="h-5 w-5" />}
            onClick={() => setIsCsvModalOpen(true)}
          >
            Importar CSV
          </Button>
          {userRole === 'Administrador' && (
            <Button
              variant="outline"
              icon={<Download className="h-5 w-5" />}
              onClick={exportExpensesToCsv}
            >
              Exportar CSV
            </Button>
          )}
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={handleAddExpense}
            className="shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
          >
            Nuevo Gasto
          </Button>
        </div>
      </div>

      {/* Info message for Producción role */}
      {userRole === 'Producción' && (
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <DollarSign className="h-12 w-12 text-indigo-600 dark:text-indigo-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Gestión de Gastos
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Como usuario de Producción, podés agregar nuevos gastos manualmente o importar desde CSV.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metrics Section - Hidden for Producción role */}
      {userRole !== 'Producción' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Amount */}
          <Card>
            <CardContent className="px-3 py-2">
              <div className="relative">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total del Período</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {formatCurrency(metrics.totalAmount)}
                </p>
                <p className={`text-sm ${metrics.changeAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {metrics.changeAmount >= 0 ? '+' : ''}{metrics.changePercentage.toFixed(1)}% vs. anterior
                </p>
                <div className="absolute top-2 right-2 w-7 h-7 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Count */}
          <Card>
            <CardContent className="px-3 py-2">
              <div className="relative">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Cantidad de Gastos</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">{metrics.count}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  En el período seleccionado
                </p>
                <div className="absolute top-2 right-2 w-7 h-7 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* VAT */}
          <Card>
            <CardContent className="px-3 py-2">
              <div className="relative">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total IVA</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {formatCurrency(metrics.totalVat)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  IVA acumulado
                </p>
                <div className="absolute top-2 right-2 w-7 h-7 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <Landmark className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Category */}
          <Card>
            <CardContent className="px-3 py-2">
              <div className="relative">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Categoría Principal</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {metrics.topCategories[0]?.name || 'N/A'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {metrics.topCategories[0] ? formatCurrency(metrics.topCategories[0].amount) : 'Sin datos'}
                </p>
                <div className="absolute top-2 right-2 w-7 h-7 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                  <Tag className="h-3.5 w-3.5 text-purple-600 dark:text-indigo-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters Dropdown */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="relative">
                <Input
                  label="Buscar"
                  id="searchQuery"
                  name="searchQuery"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por descripción o categoría..."
                />
                <div className="absolute right-3 top-9">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              
              <Select
                label="Categoría"
                value={filters.categoryId}
                onChange={(value) => setFilters(prev => ({ ...prev, categoryId: value }))}
                options={[
                  { value: 'all', label: 'Todas las categorías' },
                  ...expenseCategories.map(category => ({
                    value: category.id,
                    label: category.name
                  }))
                ]}
              />
              
              <Select
                label="Método de Pago"
                value={filters.paymentMethod}
                onChange={(value) => setFilters(prev => ({ ...prev, paymentMethod: value }))}
                options={[
                  { value: 'all', label: 'Todos los métodos' },
                  { value: 'cash', label: 'Efectivo' },
                  { value: 'transfer', label: 'Transferencia' },
                  { value: 'card', label: 'Tarjeta' },
                  { value: 'check', label: 'Cheque' },
                  { value: 'mercadopago', label: 'MercadoPago' },
                ]}
              />
              
              <Select
                label="Período"
                value={filters.dateRange}
                onChange={(value) => setFilters(prev => ({ ...prev, dateRange: value }))}
                options={[
                  { value: 'month', label: 'Mes actual' },
                  { value: 'previousMonth', label: 'Mes anterior' },
                  { value: 'today', label: 'Hoy' },
                  { value: 'week', label: 'Últimos 7 días' },
                  { value: 'year', label: 'Último año' },
                  { value: 'custom', label: 'Personalizado' },
                  { value: 'all', label: 'Todo el tiempo' },
                ]}
              />
            </div>
            
            {filters.dateRange === 'custom' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Desde"
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
                <Input
                  label="Hasta"
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={resetFilters}
              >
                Limpiar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts - Hidden for Producción role */}
      {userRole !== 'Producción' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expenses by Category Chart */}
          <Card>
            <CardHeader title="Gastos por Categoría" />
            <CardContent>
              <div className="h-80">
                {sortedExpenses.length > 0 ? (
                  <Pie data={categoryChartData} options={chartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                    No hay datos para mostrar
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Categories Bar Chart */}
          <Card>
            <CardHeader title="Top 5 Categorías" />
            <CardContent>
              <div className="h-80">
                {metrics.topCategories.length > 0 ? (
                  <div className="space-y-3">
                    {metrics.topCategories.map((category, index) => (
                      <div key={category.name} className="flex items-center">
                        <div className="w-8 text-sm font-medium text-gray-500 dark:text-gray-400">
                          #{index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {category.name}
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {formatCurrency(category.amount)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${(category.amount / metrics.topCategories[0]?.amount) * 100}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                    No hay datos para mostrar
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Expenses Table - Hidden for Producción role */}
      {userRole !== 'Producción' && (
        <Card>
          <CardHeader title={`Gastos (${sortedExpenses.length})`}>
            <div className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">
              Total: {formatCurrency(totalAmount)}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Categoría
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Descripción
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Monto
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Método
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedExpenses.length > 0 ? (
                      sortedExpenses.map((expense) => (
                        <tr key={expense.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                              {formatArgentinaDate(expense.date)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {getCategoryName(expense.category_id)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {expense.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-red-600 dark:text-red-400">
                            {formatCurrency(expense.amount)}
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400">
                            {getPaymentMethodName(expense.payment_method)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit className="h-4 w-4" />}
                                onClick={() => handleEditExpense(expense)}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => handleDeleteExpense(expense.id)}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                          No hay gastos que coincidan con los filtros seleccionados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Modals */}
      <ExpenseModal
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        onSave={handleSaveExpense}
        initialData={selectedExpense || undefined}
      />
      
      <ExpenseCsvModal
        isOpen={isCsvModalOpen}
        onClose={() => setIsCsvModalOpen(false)}
      />
      
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDeleteExpense}
        title="Eliminar Gasto"
        message="¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default Expenses;